'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Check, ChevronDown, ChevronUp, Zap, TrendingDown, Star, GitCompare, Clock, Droplets, Heart, Bell } from 'lucide-react';
import type { HealthPackage } from './packagesData';

export default function PackageCard({
  pkg,
  isInCompare,
  onToggleCompare,
  isFavourite,
  onToggleFavourite,
  onPriceAlert,
}: {
  pkg: HealthPackage;
  isInCompare: boolean;
  onToggleCompare: () => void;
  isFavourite: boolean;
  onToggleFavourite: () => void;
  onPriceAlert: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`relative rounded-2xl overflow-hidden flex flex-col card-hover transition-all duration-200 ${
        pkg.highlight
          ? 'ring-2 ring-primary shadow-card-hover'
          : 'border border-border bg-card'
      }`}
    >
      {/* Popular badge */}
      {pkg.badge && (
        <div className={`px-4 py-2 flex items-center justify-center gap-1.5 ${
          pkg.popular ? 'bg-primary' : 'bg-gradient-to-r from-amber-500 to-orange-500'
        }`}>
          {pkg.popular && <Star size={11} className="text-yellow-300 fill-yellow-300" />}
          <span className="text-white text-xs font-bold uppercase tracking-wider">{pkg.badge}</span>
        </div>
      )}

      <div className="p-5 flex flex-col flex-1 bg-card">
        {/* Category + discount + action icons */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {pkg.category}
          </span>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              <TrendingDown size={10} />
              <span className="text-xs font-bold">{pkg.discount}% OFF</span>
            </div>
            {/* Price Alert Bell */}
            <button
              onClick={onPriceAlert}
              title="Get notified when price drops"
              className="p-1.5 rounded-full hover:bg-amber-50 transition-colors group"
              aria-label="Set price alert"
            >
              <Bell size={14} className="text-muted-foreground group-hover:text-amber-500 transition-colors" />
            </button>
            {/* Favourite Heart */}
            <button
              onClick={onToggleFavourite}
              title={isFavourite ? 'Remove from favourites' : 'Save to favourites'}
              className="p-1.5 rounded-full hover:bg-red-50 transition-colors"
              aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
            >
              <Heart
                size={14}
                className={`transition-colors ${isFavourite ? 'text-red-500 fill-red-500' : 'text-muted-foreground hover:text-red-400'}`}
              />
            </button>
          </div>
        </div>

        {/* Name */}
        <h3 className="font-bold text-base text-foreground mb-1 leading-tight">{pkg.name}</h3>
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{pkg.description}</p>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="stats-number text-2xl font-extrabold text-primary">
            ₹{pkg.price.toLocaleString('en-IN')}
          </span>
          <span className="text-sm text-muted-foreground line-through">
            ₹{pkg.originalPrice.toLocaleString('en-IN')}
          </span>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-2 flex-wrap mb-4 pb-4 border-b border-border">
          <div className="flex items-center gap-1 bg-secondary rounded-lg px-2 py-1">
            <Zap size={10} className="text-primary" />
            <span className="text-xs font-bold text-primary">{pkg.tests} Tests</span>
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
            <Clock size={10} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{pkg.turnaround}</span>
          </div>
          {pkg.fasting && (
            <div className="flex items-center gap-1 bg-amber-50 rounded-lg px-2 py-1">
              <Droplets size={10} className="text-amber-600" />
              <span className="text-xs text-amber-700 font-medium">Fasting</span>
            </div>
          )}
        </div>

        {/* Included tests */}
        <div className="flex-1 mb-4">
          <ul className="space-y-1.5">
            {(expanded ? pkg.includes : pkg.includes.slice(0, 4)).map((item) => (
              <li key={`${pkg.id}-inc-${item}`} className="flex items-start gap-2 text-xs text-foreground">
                <Check size={12} className="text-green-600 flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          {pkg.includes.length > 4 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-primary font-semibold mt-2 hover:text-blue-700 transition-colors"
            >
              {expanded ? (
                <>Show less <ChevronUp size={12} /></>
              ) : (
                <>+{pkg.includes.length - 4} more tests <ChevronDown size={12} /></>
              )}
            </button>
          )}
        </div>

        {/* Sample type */}
        <div className="text-xs text-muted-foreground mb-4">
          <span className="font-medium">Sample:</span> {pkg.sampleType}
        </div>

        {/* Actions */}
        <div className="space-y-2 mt-auto">
          <Link
            href={`/book-appointment?package=${pkg.id}`}
            className={`w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all duration-150 block ${
              pkg.highlight
                ? 'bg-primary text-white hover:bg-blue-700 shadow-card'
                : 'border-2 border-primary text-primary hover:bg-secondary'
            }`}
          >
            Book Now
          </Link>

          <button
            onClick={onToggleCompare}
            className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-colors ${
              isInCompare
                ? 'bg-primary/10 text-primary border border-primary' :'bg-muted text-muted-foreground hover:bg-secondary hover:text-primary border border-transparent'
            }`}
          >
            <GitCompare size={12} />
            {isInCompare ? 'Added to Compare' : 'Add to Compare'}
          </button>
        </div>
      </div>
    </div>
  );
}